import { Head, Link, router } from '@inertiajs/react'
import { useRef, useState, useMemo, useEffect } from 'react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import {
  Dropdown, DropdownButton, DropdownMenu, DropdownItem, DropdownLabel,
} from '@/components/catalyst/dropdown'
import {
  ClipboardDocumentListIcon, ArrowUpTrayIcon, ArrowDownTrayIcon,
  TableCellsIcon, ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import PivotModal from './PivotModal'
import { exportVpoNewToXlsx, type VpoNewRow } from './vpoNewExport'

// ─── Vpo/New — full 1:1 port of legacy app/javascript/app/pages/Vpo/New.jsx
//
// Toolbar (left → right):
//   • Back to list (legacy "VPO Table" button)
//   • Create VPO  ▾  →  Create From UPC  /  Create From Style+Color+Size
//                       (each = native multipart POST to /vpo/create with
//                        the matching `mode` param)
//   • Export VPO  ▾  →  Export VPO (.xlsx, ExcelJS with formulas)
//                       Export VPO As Pivot Table  (opens PivotModal)
//
// Table:
//   • 27 columns matching the legacy column order
//   • Per-column filters (DataTable's built-in popover filter) for
//     14 client-side columns + 2 backend-routed ones (Brand & State)
//   • Footer summary row aggregates total_cost / total_selling /
//     original_order / ordered / bo / confirmed / received / invoiced
//     against the *visible* (filtered+sorted) rows
//
// PivotModal — separate component; receives unique Order IDs from the
// currently-visible rows so the export reflects what the user is looking at.

interface VpoNewProps {
  brands?: string[]
  states?: string[]
  vendors?: string[]
  data_source?: VpoNewRow[]
}

// Starting widths per column — pulled from the legacy column array's
// width prop. DataTable persists user resizes per-browser in
// localStorage under `vpo-new-column-widths`.
const VPO_NEW_COL_WIDTHS: Record<string, number> = {
  customer:               190,
  brand:                  140,
  customer_po_number:     130,
  order_id:               100,
  ghost_id:               140,
  state:                  150,
  vendor:                 150,
  vendor_po_number:       120,
  vendor_po_status:       150,
  upc:                    120,
  style_number:           120,
  style_description:      180,
  color:                  80,
  size:                   70,
  cost_price:             100,
  total_cost:             110,
  selling_price:          110,
  total_selling:          120,
  original_order:         110,
  ordered:                90,
  bo:                     70,
  confirmed:              100,
  received:               100,
  invoiced:               100,
  vendor_order_date:      140,
  vendor_start_ship_date: 180,
  vendor_cancel_date:     170,
}

// Number helpers — sum a numeric column over visible rows, format with
// US locale + 2 decimals for currency, integer for counts.
function sumNumeric(rows: VpoNewRow[], key: keyof VpoNewRow): number {
  return rows.reduce((acc, r) => {
    const v = r[key]
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '0'))
    return acc + (Number.isFinite(n) ? n : 0)
  }, 0)
}
function fmtMoney(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
function fmtInt(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export default function VpoNew({ brands = [], states = [], vendors = [], data_source = [] }: VpoNewProps) {
  // ─── Upload state ─────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [notif, setNotif] = useState<{ msg: string; show: boolean }>({ msg: '', show: false })
  const showNotif = (msg: string) => setNotif({ msg, show: true })

  const csrfToken =
    (typeof document !== 'undefined' &&
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content) ||
    ''

  const triggerUpload = (mode: string) => {
    setPendingMode(mode)
    fileInputRef.current?.click()
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !pendingMode) return

    setUploading(true)
    const fd = new FormData()
    fd.append('vpo_file', file)
    fd.append('mode', pendingMode)

    try {
      const res = await fetch('/vpo/create', { method: 'POST', body: fd, headers: { 'X-CSRF-Token': csrfToken } })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showNotif(data.message || `Upload failed (${res.status})`)
        return
      }
      const ids: number[] = data.vpo_ids || []
      if (ids.length === 0) {
        showNotif('No VPOs were created from this file.')
        return
      }
      showNotif(`Created VPO ${ids.join(', ')}. Redirecting…`)
      setTimeout(() => router.visit(`/vpo/show/${ids[0]}`), 600)
    } catch (err) {
      showNotif(`Network error: ${(err as Error).message}`)
    } finally {
      setUploading(false)
    }
  }

  // ─── Backend filter sync (Brand & State) ──────
  // Legacy sends POST /vpo/filter when these change; Rails re-renders the
  // same page with a different data_source. preserveState keeps local UI
  // (selected sort, table filters on other columns) in place.
  const [brandFilter, setBrandFilter] = useState<string[]>([])
  const [stateFilter, setStateFilter] = useState<string[]>([])
  const submitBackendFilter = (brands_: string[], states_: string[]) => {
    router.post('/vpo/filter', { brands: brands_, states: states_ }, {
      preserveState: true,
      preserveScroll: true,
      onError: () => showNotif('Filter request failed.'),
    })
  }

  // ─── Pivot modal state ────────────────────────
  const [pivotOpen, setPivotOpen] = useState(false)
  const [visibleRows, setVisibleRows] = useState<VpoNewRow[]>(data_source)

  // Re-sync visibleRows when data_source itself changes (e.g. after the
  // brand/state backend filter round-trips).
  useEffect(() => { setVisibleRows(data_source) }, [data_source])

  const orderIdsFromVisible = useMemo(() => {
    const s = new Set<string>()
    for (const r of visibleRows) if (r.order_id) s.add(String(r.order_id))
    return Array.from(s)
  }, [visibleRows])

  // ─── Columns (27 total, legacy column order) ──
  const cell = (v: unknown) => <span className="truncate">{v == null || v === '' ? '—' : String(v)}</span>
  const money = (v: unknown) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '0'))
    return <span className="tabular-nums">{Number.isFinite(n) && n !== 0 ? `$${n.toFixed(2)}` : '—'}</span>
  }
  const sortStr = (k: keyof VpoNewRow) => (r: VpoNewRow) => String(r[k] ?? '')
  const sortNum = (k: keyof VpoNewRow) => (r: VpoNewRow) => parseFloat(String(r[k] ?? '0')) || 0

  const columns: DataTableColumn<VpoNewRow>[] = [
    { key: 'customer',               label: 'Customer',                render: r => cell(r.customer),               filterValue: r => r.customer ?? '',               sortValue: sortStr('customer') },
    { key: 'brand',                  label: 'Brand',                   render: r => cell(r.brand),                  filterValue: r => r.brand ?? '',                  sortValue: sortStr('brand') },
    { key: 'customer_po_number',     label: 'Customer PO#',            render: r => cell(r.customer_po_number),     filterValue: r => r.customer_po_number ?? '' },
    { key: 'order_id',               label: 'Order ID',                render: r => r.order_id
                                                                          ? <a href={`/customer_orders/${r.order_id}`} target="_blank" rel="noopener" className="text-indigo-600 hover:underline">{r.order_id}</a>
                                                                          : cell(null),
                                                                       filterValue: r => r.order_id ?? '',                sortValue: sortStr('order_id') },
    { key: 'ghost_id',               label: 'Ghost Platform ID',       render: r => cell(r.ghost_id),               filterValue: r => r.ghost_id ?? '',               sortValue: sortStr('ghost_id') },
    { key: 'state',                  label: 'Status',                  render: r => cell(r.state),                  filterValue: r => r.state ?? '' },
    { key: 'vendor',                 label: 'Vendor',                  render: r => cell(r.vendor),                 filterValue: r => r.vendor ?? '' },
    { key: 'vendor_po_number',       label: 'Vendor PO#',              render: r => cell(r.vendor_po_number),       filterValue: r => r.vendor_po_number ?? '' },
    { key: 'vendor_po_status',       label: 'Vendor PO Status',        render: r => cell(r.vendor_po_status),       filterValue: r => r.vendor_po_status ?? '' },
    { key: 'upc',                    label: 'UPC',                     render: r => cell(r.upc),                    filterValue: r => r.upc ?? '',                    sortValue: sortNum('upc') },
    { key: 'style_number',           label: 'Style number',            render: r => cell(r.style_number),           filterValue: r => r.style_number ?? '' },
    { key: 'style_description',      label: 'Style description',       render: r => cell(r.style_description),      noFilter: true },
    { key: 'color',                  label: 'Color',                   render: r => cell(r.color),                  noFilter: true },
    { key: 'size',                   label: 'Size',                    render: r => cell(r.size),                   noFilter: true },
    { key: 'cost_price',             label: 'Cost Price',              render: r => money(r.cost_price),            noFilter: true },
    { key: 'total_cost',             label: 'Total Cost',              render: r => money(r.total_cost),            noFilter: true },
    { key: 'selling_price',          label: 'Selling Price',           render: r => money(r.selling_price),         noFilter: true },
    { key: 'total_selling',          label: 'Total Selling',           render: r => money(r.total_selling),         noFilter: true },
    { key: 'original_order',         label: 'Original Order',          render: r => cell(r.original_order),         noFilter: true },
    { key: 'ordered',                label: 'Ordered',                 render: r => cell(r.ordered),                noFilter: true },
    { key: 'bo',                     label: 'BO',                      render: r => cell(r.bo),                     filterValue: r => String(r.bo ?? '') },
    { key: 'confirmed',              label: 'Confirmed',               render: r => cell(r.confirmed),              noFilter: true },
    { key: 'received',               label: 'Received',                render: r => cell(r.received),               noFilter: true },
    { key: 'invoiced',               label: 'Invoiced',                render: r => cell(r.invoiced),               noFilter: true },
    { key: 'vendor_order_date',      label: 'Vendor Order Date',       render: r => cell(r.vendor_order_date),      filterValue: r => r.vendor_order_date ?? '' },
    { key: 'vendor_start_ship_date', label: 'Vendor Start Ship',       render: r => cell(r.vendor_start_ship_date), filterValue: r => r.vendor_start_ship_date ?? '' },
    { key: 'vendor_cancel_date',     label: 'Vendor Cancel Date',      render: r => cell(r.vendor_cancel_date),     filterValue: r => r.vendor_cancel_date ?? '' },
  ]

  // ─── Footer summary (aggregates over visible rows) ──────────────
  const summary = (rowsForSummary: VpoNewRow[]) => {
    const sumTotalCost    = sumNumeric(rowsForSummary, 'total_cost')
    const sumTotalSelling = sumNumeric(rowsForSummary, 'total_selling')
    const sumOriginal     = sumNumeric(rowsForSummary, 'original_order')
    const sumOrdered      = sumNumeric(rowsForSummary, 'ordered')
    const sumBo           = sumNumeric(rowsForSummary, 'bo')
    const sumConfirmed    = sumNumeric(rowsForSummary, 'confirmed')
    const sumReceived     = sumNumeric(rowsForSummary, 'received')
    const sumInvoiced     = sumNumeric(rowsForSummary, 'invoiced')
    return (
      <tr className="border-t-2 border-zinc-300 font-semibold text-zinc-800 dark:border-zinc-600 dark:text-zinc-200" data-testid="vpo-new-summary">
        {/* 0…14: empty (customer → cost_price) */}
        {Array.from({ length: 15 }).map((_, i) => <td key={`s-${i}`} className="px-3 py-2"></td>)}
        <td className="px-3 py-2 tabular-nums">{fmtMoney(sumTotalCost)}</td>
        <td className="px-3 py-2"></td>
        <td className="px-3 py-2 tabular-nums">{fmtMoney(sumTotalSelling)}</td>
        <td className="px-3 py-2 tabular-nums">{fmtInt(sumOriginal)}</td>
        <td className="px-3 py-2 tabular-nums">{fmtInt(sumOrdered)}</td>
        <td className="px-3 py-2 tabular-nums">{fmtInt(sumBo)}</td>
        <td className="px-3 py-2 tabular-nums">{fmtInt(sumConfirmed)}</td>
        <td className="px-3 py-2 tabular-nums">{fmtInt(sumReceived)}</td>
        <td className="px-3 py-2 tabular-nums">{fmtInt(sumInvoiced)}</td>
        {/* trailing 3 date columns */}
        <td className="px-3 py-2"></td>
        <td className="px-3 py-2"></td>
        <td className="px-3 py-2"></td>
      </tr>
    )
  }

  // ─── Render ────────────────────────────────────
  return (
    <>
      <Head title="Create VPO" />
      <Notification message={notif.msg} show={notif.show} onClose={() => setNotif({ msg: '', show: false })} />

      <PageHeader
        title="Create VPO"
        subtitle={data_source.length > 0
          ? `${data_source.length} product${data_source.length === 1 ? '' : 's'} from selected orders`
          : 'Upload an xlsx or visit from the dashboard with selected orders.'
        }
        icon={<ClipboardDocumentListIcon className="h-5 w-5" />}
        actions={
          <div className="flex items-center gap-2" data-testid="vpo-new-toolbar">
            <Link href="/vpo/index"
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
              data-testid="vpo-new-back">
              <ArrowLeftIcon className="h-4 w-4" /> Back
            </Link>

            {/* Create VPO ▾ */}
            <Dropdown>
              <DropdownButton as="button" data-testid="vpo-create-trigger"
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700">
                <ArrowUpTrayIcon className="h-4 w-4" /> Create VPO
              </DropdownButton>
              <DropdownMenu anchor="bottom end" className="z-50 min-w-56">
                <DropdownItem onClick={() => triggerUpload('upc')} data-testid="vpo-create-mode-upc">
                  <DropdownLabel>Create From UPC</DropdownLabel>
                </DropdownItem>
                <DropdownItem onClick={() => triggerUpload('style_color_size')} data-testid="vpo-create-mode-style">
                  <DropdownLabel>Create From Style # + Color + Size</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>

            {/* Export VPO ▾ */}
            <Dropdown>
              <DropdownButton as="button" data-testid="vpo-export-trigger"
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800">
                <ArrowDownTrayIcon className="h-4 w-4" /> Export VPO
              </DropdownButton>
              <DropdownMenu anchor="bottom end" className="z-50 min-w-56">
                <DropdownItem
                  onClick={() => exportVpoNewToXlsx(visibleRows, vendors).catch(err => showNotif(`Export failed: ${err.message}`))}
                  data-testid="vpo-export-xlsx">
                  <DropdownLabel>Export VPO (.xlsx)</DropdownLabel>
                </DropdownItem>
                <DropdownItem onClick={() => setPivotOpen(true)} data-testid="vpo-export-pivot">
                  <DropdownLabel>Export VPO As Pivot Table</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        }
      />

      {/* Backend Brand + State filters — surface them above the table so
          the user can see the round-trip is happening. */}
      {(brands.length > 0 || states.length > 0) && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50/50 p-2 text-xs dark:border-zinc-700 dark:bg-zinc-800/30" data-testid="vpo-new-backend-filters">
          <BackendMultiSelect label="Brand" all={brands} value={brandFilter}
            onChange={(v) => { setBrandFilter(v); submitBackendFilter(v, stateFilter) }} />
          <BackendMultiSelect label="State" all={states} value={stateFilter}
            onChange={(v) => { setStateFilter(v); submitBackendFilter(brandFilter, v) }} />
        </div>
      )}

      {/* Hidden file input — re-targeted per mode via triggerUpload. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="hidden"
        onChange={onFile}
        data-testid="vpo-file-input"
      />
      {uploading && (
        <p className="mb-3 text-xs text-zinc-500" data-testid="vpo-uploading">
          Uploading and parsing… this can take 10–30 seconds for large files.
        </p>
      )}

      <DataTable<VpoNewRow>
        rows={data_source}
        columns={columns}
        rowKey={(r) => `${r.order_id ?? ''}-${r.upc ?? ''}-${r.style_number ?? ''}-${r.size ?? ''}` as unknown as string}
        emptyText="No products to show. Upload a Create From … file or select orders on the dashboard."
        filteredEmptyText="No products match the current filters."
        summary={summary}
        onVisibleRowsChange={setVisibleRows}
        // Fixed-width columns + per-column resize handles. The table stops
        // squeezing to fit the parent — content scrolls horizontally. User
        // widths persist per browser via localStorage.
        resizable
        widthsStorageKey="vpo-new-column-widths"
        widths={VPO_NEW_COL_WIDTHS}
      />

      <PivotModal
        open={pivotOpen}
        onClose={() => setPivotOpen(false)}
        orderIds={orderIdsFromVisible}
        csrfToken={csrfToken}
        onError={showNotif}
      />
    </>
  )
}

VpoNew.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

// ─── BackendMultiSelect — small popover wrapping native checkboxes for
//     the two backend-routed filters (Brand & State). On every toggle the
//     parent fires the POST. Plain enough to live inline. ────────────
function BackendMultiSelect({ label, all, value, onChange }: {
  label: string
  all: string[]
  value: string[]
  onChange: (v: string[]) => void
}) {
  const [open, setOpen] = useState(false)
  const toggle = (v: string) => {
    if (value.includes(v)) onChange(value.filter(x => x !== v))
    else onChange([...value, v])
  }
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
        data-testid={`vpo-backend-filter-${label.toLowerCase()}-toggle`}>
        {label}{value.length > 0 && <span className="rounded bg-indigo-100 px-1 text-[10px] text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">{value.length}</span>}
      </button>
      {open && (
        <div className="absolute z-40 mt-1 max-h-72 w-56 overflow-y-auto rounded-lg border border-zinc-200 bg-white p-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-800"
          data-testid={`vpo-backend-filter-${label.toLowerCase()}-panel`}>
          {all.length === 0 && <p className="px-2 py-1 text-xs text-zinc-400">No options</p>}
          {all.map(v => (
            <label key={v} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-700/50">
              <input type="checkbox" checked={value.includes(v)} onChange={() => toggle(v)}
                className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600 dark:border-zinc-600 dark:bg-zinc-800" />
              <span className="truncate">{v}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
